import React, { useState, useCallback } from 'react';
import {
  useSalesTargetConfig,
  useSalesTargetOverrides,
  useUpdateDefaultAmount,
  useCreateOverride,
  useUpdateOverride,
  useDeleteOverride,
  SalesTargetOverride,
} from '../hooks/useSalesTargets';
import { usePredictiveModStatus, usePredictiveModConfig } from '../hooks/usePredictiveModControl';

// Tipos para notificaciones
interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  title?: string;
}

export const SalesTargetPanel: React.FC = () => {
  const { config, loading: configLoading, refetch: refetchConfig } = useSalesTargetConfig();
  const { overrides, loading: overridesLoading, refetch: refetchOverrides } = useSalesTargetOverrides();
  const { updateDefaultAmount, updating: updatingDefault } = useUpdateDefaultAmount();
  const { createOverride, creating } = useCreateOverride();
  const { updateOverride, updating } = useUpdateOverride();
  const { deleteOverride, deleting } = useDeleteOverride();

  // Métricas del Control Predictivo para mostrar progreso del día
  const { status: predictiveStatus, loading: predictiveLoading } = usePredictiveModStatus(5000);
  const { config: predictiveConfig } = usePredictiveModConfig();

  // Sistema de notificaciones
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Estados para modales
  const [showDefaultModal, setShowDefaultModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ id: string; description: string } | null>(null);

  // Estados para formularios
  const [newDefaultAmount, setNewDefaultAmount] = useState('');
  const [editingOverride, setEditingOverride] = useState<SalesTargetOverride | null>(null);
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    amount: '',
    description: '',
  });

  // Función para mostrar notificaciones
  const showNotification = useCallback((type: Notification['type'], message: string, title?: string) => {
    const id = Date.now().toString();
    const notification: Notification = { id, type, message, title };
    setNotifications(prev => [...prev, notification]);

    // Auto-remover después de 4 segundos
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  // Función para cerrar notificación manualmente
  const closeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleUpdateDefaultAmount = async () => {
    const amount = parseFloat(newDefaultAmount);
    if (isNaN(amount) || amount < 0) {
      showNotification('warning', 'Por favor ingrese un monto válido', 'Validación');
      return;
    }

    try {
      await updateDefaultAmount(amount);
      setShowDefaultModal(false);
      setNewDefaultAmount('');
      await refetchConfig();
      showNotification('success', 'Monto predeterminado actualizado correctamente', 'Actualizado');
    } catch (error) {
      showNotification('error', 'Error al actualizar el monto predeterminado', 'Error');
    }
  };

  const handleCreateOverride = async () => {
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount < 0) {
      showNotification('warning', 'Por favor ingrese un monto válido', 'Validación');
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      showNotification('warning', 'Por favor seleccione las fechas', 'Validación');
      return;
    }

    try {
      await createOverride({
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        amount,
        description: formData.description,
      });
      setShowCreateModal(false);
      setFormData({ startDate: '', endDate: '', amount: '', description: '' });
      await refetchOverrides();
      showNotification('success', 'Monto especial creado correctamente', 'Creado');
    } catch (error: any) {
      showNotification('error', error.message || 'Error al crear el monto especial', 'Error');
    }
  };

  const handleEditOverride = async () => {
    if (!editingOverride) return;

    const amount = formData.amount ? parseFloat(formData.amount) : undefined;
    if (amount !== undefined && (isNaN(amount) || amount < 0)) {
      showNotification('warning', 'Por favor ingrese un monto válido', 'Validación');
      return;
    }

    try {
      await updateOverride({
        id: editingOverride.id,
        startDate: formData.startDate ? new Date(formData.startDate) : undefined,
        endDate: formData.endDate ? new Date(formData.endDate) : undefined,
        amount,
        description: formData.description || undefined,
      });
      setShowEditModal(false);
      setEditingOverride(null);
      setFormData({ startDate: '', endDate: '', amount: '', description: '' });
      await refetchOverrides();
      showNotification('success', 'Monto especial actualizado correctamente', 'Actualizado');
    } catch (error: any) {
      showNotification('error', error.message || 'Error al actualizar el monto especial', 'Error');
    }
  };

  const handleDeleteOverride = async (id: string) => {
    // Buscar la descripción del override para mostrar en el modal
    const override = overrides.find(o => o.id === id);
    const description = override?.description || `del ${formatDate(override?.startDate || '')} al ${formatDate(override?.endDate || '')}`;
    setConfirmAction({ id, description });
    setShowConfirmModal(true);
  };

  const confirmDeleteOverride = async () => {
    if (!confirmAction) return;

    try {
      await deleteOverride(confirmAction.id);
      await refetchOverrides();
      showNotification('success', 'Monto especial eliminado correctamente', 'Eliminado');
    } catch (error) {
      showNotification('error', 'Error al eliminar el monto especial', 'Error');
    } finally {
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  };

  const openEditModal = (override: SalesTargetOverride) => {
    setEditingOverride(override);
    setFormData({
      startDate: new Date(override.startDate).toISOString().split('T')[0],
      endDate: new Date(override.endDate).toISOString().split('T')[0],
      amount: override.amount.toString(),
      description: override.description || '',
    });
    setShowEditModal(true);
  };

  if (configLoading) {
    return <div className="loading">Cargando configuración...</div>;
  }

  return (
    <div className="sales-target-panel">
      <h2>Configuración de Montos de Ventas</h2>

      {/* Cards principales */}
      <div className="main-cards-grid">
        {/* Monto Predeterminado */}
        <div className="amount-card primary-card">
          <div className="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <h3>Monto Predeterminado Diario</h3>
          <p className="amount-value">{config ? formatCurrency(config.defaultAmount) : '-'}</p>
          <p className="amount-label">Meta de ventas en efectivo por día</p>
          <button
            className="btn-primary"
            onClick={() => {
              setNewDefaultAmount(config?.defaultAmount.toString() || '');
              setShowDefaultModal(true);
            }}
          >
            Cambiar Monto
          </button>
        </div>

        {/* Progreso de Impuestos del Día */}
        <div className="amount-card metrics-card">
          <div className="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 20V10"/>
              <path d="M12 20V4"/>
              <path d="M6 20v-6"/>
            </svg>
          </div>
          <h3>Progreso de Impuestos Hoy</h3>
          {predictiveLoading ? (
            <p className="loading-text">Cargando...</p>
          ) : predictiveStatus ? (
            <>
              <div className="metrics-row">
                <span>Impuestos generados:</span>
                <strong className="metric-value">{formatCurrency(predictiveStatus.currentTaxAmount)}</strong>
              </div>
              <div className="metrics-row">
                <span>Meta del día:</span>
                <strong className="metric-value">{formatCurrency(predictiveStatus.currentTaxTarget)}</strong>
              </div>
              <div className="progress-container">
                <div
                  className={`progress-bar-fill ${predictiveStatus.currentPercentage >= 100 ? 'complete' : ''}`}
                  style={{ width: `${Math.min(predictiveStatus.currentPercentage, 100)}%` }}
                />
                <span className="progress-text">{predictiveStatus.currentPercentage.toFixed(1)}%</span>
              </div>
              <div className={`mod-status-badge ${predictiveStatus.modEnabled ? 'active' : 'inactive'}`}>
                MOD: {predictiveStatus.modEnabled ? 'ACTIVO' : 'INACTIVO'}
              </div>
            </>
          ) : (
            <p className="no-data-text">Sin datos disponibles</p>
          )}
        </div>

        {/* Estado del Control Predictivo */}
        <div className="amount-card status-card">
          <div className="card-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </div>
          <h3>Control Automático</h3>
          <div className={`control-status ${predictiveConfig?.enabled ? 'enabled' : 'disabled'}`}>
            <span className="status-dot"></span>
            <span>{predictiveConfig?.enabled ? 'ACTIVO' : 'INACTIVO'}</span>
          </div>
          <p className="status-description">
            {predictiveConfig?.enabled
              ? 'El MOD se ajusta automáticamente según la meta de impuestos'
              : 'El MOD se controla manualmente desde el toggle del header'}
          </p>
          {predictiveStatus && predictiveConfig?.enabled && (
            <div className="last-decision">
              <span className="decision-label">Última decisión:</span>
              <span className={`decision-value ${predictiveStatus.lastDecision?.toLowerCase()}`}>
                {predictiveStatus.lastDecision || 'N/A'}
              </span>
            </div>
          )}
          <a href="/control-predictivo" className="link-to-config">
            Ir a Control Predictivo →
          </a>
        </div>
      </div>

      {/* Nota informativa */}
      <div className="info-banner">
        <span className="info-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        </span>
        <p>
          El control automático del MOD ahora se maneja desde la sección <strong>"Control Predictivo"</strong> basado
          en la meta de impuestos. Esta página solo configura el monto de referencia para ventas.
        </p>
      </div>

      {/* Botón para agregar monto por fechas */}
      <div className="action-bar">
        <button
          className="btn-success"
          onClick={() => {
            setFormData({ startDate: '', endDate: '', amount: '', description: '' });
            setShowCreateModal(true);
          }}
        >
          + Agregar Monto Por Fechas
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            refetchConfig();
            refetchOverrides();
          }}
        >
          Actualizar
        </button>
      </div>

      {/* Tabla de Montos Especiales */}
      <div className="overrides-section">
        <h3>Montos Especiales por Fechas</h3>

        {overridesLoading ? (
          <div className="loading">Cargando montos especiales...</div>
        ) : overrides.length === 0 ? (
          <div className="empty-state">
            <p>No hay montos especiales configurados</p>
            <p className="empty-hint">
              Los montos especiales permiten definir metas diferentes para períodos específicos
              como navidad, temporada alta, rebajas, etc.
            </p>
          </div>
        ) : (
          <table className="overrides-table">
            <thead>
              <tr>
                <th>Fecha Inicial</th>
                <th>Fecha Final</th>
                <th>Monto</th>
                <th>Descripción</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((override) => (
                <tr key={override.id}>
                  <td>{formatDate(override.startDate)}</td>
                  <td>{formatDate(override.endDate)}</td>
                  <td className="amount">{formatCurrency(override.amount)}</td>
                  <td>{override.description || '-'}</td>
                  <td className="actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => openEditModal(override)}
                      title="Editar"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDeleteOverride(override.id)}
                      title="Eliminar"
                      disabled={deleting}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal: Cambiar Monto Predeterminado */}
      {showDefaultModal && (
        <div className="modal-overlay" onClick={() => setShowDefaultModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Cambiar Monto Predeterminado</h3>
            <p>Este será el monto objetivo para todos los días (excepto fechas con monto especial)</p>

            <div className="form-group">
              <label>Monto Diario (COP)</label>
              <input
                type="number"
                value={newDefaultAmount}
                onChange={(e) => setNewDefaultAmount(e.target.value)}
                placeholder="Ej: 1500000"
                min="0"
                step="1000"
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn-primary"
                onClick={handleUpdateDefaultAmount}
                disabled={updatingDefault}
              >
                {updatingDefault ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowDefaultModal(false)}
                disabled={updatingDefault}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Crear Monto Por Fechas */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Agregar Monto Por Fechas</h3>
            <p>Define un monto especial para un rango de fechas específico</p>

            <div className="form-group">
              <label>Fecha Inicial</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Fecha Final</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Monto (COP)</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="Ej: 2000000"
                min="0"
                step="1000"
              />
            </div>

            <div className="form-group">
              <label>Descripción (opcional)</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ej: Navidad, Temporada alta, Rebajas"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-success" onClick={handleCreateOverride} disabled={creating}>
                {creating ? 'Creando...' : 'Crear'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar Monto Especial */}
      {showEditModal && editingOverride && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Editar Monto Especial</h3>
            <p>Modifica el rango de fechas o el monto</p>

            <div className="form-group">
              <label>Fecha Inicial</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Fecha Final</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Monto (COP)</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="Ej: 2000000"
                min="0"
                step="1000"
              />
            </div>

            <div className="form-group">
              <label>Descripción (opcional)</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ej: Navidad, Temporada alta, Rebajas"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-primary" onClick={handleEditOverride} disabled={updating}>
                {updating ? 'Guardando...' : 'Guardar Cambios'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={updating}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación para Eliminar */}
      {showConfirmModal && confirmAction && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal confirm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h3>Confirmar Eliminación</h3>
            <p>¿Está seguro de eliminar el monto especial <strong>"{confirmAction.description}"</strong>?</p>
            <p className="confirm-warning-text">Esta acción no se puede deshacer.</p>
            <div className="modal-actions">
              <button
                className="btn-danger"
                onClick={confirmDeleteOverride}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
                disabled={deleting}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sistema de Notificaciones */}
      <div className="notifications-container">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`notification notification-${notification.type}`}
          >
            <div className="notification-icon">
              {notification.type === 'success' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              )}
              {notification.type === 'error' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              )}
              {notification.type === 'warning' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              )}
              {notification.type === 'info' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
              )}
            </div>
            <div className="notification-content">
              {notification.title && <span className="notification-title">{notification.title}</span>}
              <span className="notification-message">{notification.message}</span>
            </div>
            <button
              className="notification-close"
              onClick={() => closeNotification(notification.id)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Estilos */}
      <style>{`
        /* Grid de cards principales */
        .main-cards-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        @media (max-width: 1200px) {
          .main-cards-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .main-cards-grid {
            grid-template-columns: 1fr;
          }
        }

        .amount-card {
          background: var(--bg-primary, #ffffff);
          border-radius: 8px;
          padding: 16px 20px;
          border: 1px solid var(--border-color, #e2e8f0);
          border-left: 4px solid #3b82f6;
          transition: box-shadow 0.2s;
        }

        .amount-card:hover {
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
        }

        .card-icon {
          color: #3b82f6;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
        }

        .amount-card h3 {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-secondary, #64748b);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0 0 8px 0;
        }

        /* Card primaria (monto) */
        .primary-card {
          border-left-color: #2563eb;
        }

        .primary-card .card-icon {
          color: #2563eb;
        }

        .primary-card .amount-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-primary, #1e293b);
          margin: 0 0 4px 0;
        }

        .primary-card .amount-label {
          font-size: 0.8rem;
          color: var(--text-secondary, #64748b);
          margin: 0 0 12px 0;
        }

        .primary-card .btn-primary {
          background: #3b82f6;
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .primary-card .btn-primary:hover {
          background: #2563eb;
        }

        /* Card de métricas */
        .metrics-card {
          border-left-color: #3b82f6;
        }

        .metrics-card .card-icon {
          color: #3b82f6;
        }

        .metrics-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          border-bottom: 1px solid var(--border-color, #e2e8f0);
        }

        .metrics-row:last-of-type {
          border-bottom: none;
        }

        .metrics-row span {
          font-size: 0.8rem;
          color: var(--text-secondary, #64748b);
        }

        .metric-value {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-primary, #1e293b);
        }

        .progress-container {
          position: relative;
          height: 24px;
          background: var(--bg-secondary, #f1f5f9);
          border-radius: 12px;
          margin: 12px 0;
          overflow: hidden;
        }

        .progress-bar-fill {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
          border-radius: 12px;
          transition: width 0.5s ease;
        }

        .progress-bar-fill.complete {
          background: linear-gradient(90deg, #1d4ed8 0%, #1e40af 100%);
        }

        .progress-text {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 0.75rem;
          font-weight: 700;
          color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }

        .mod-status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .mod-status-badge.active {
          background: #dbeafe;
          border: 1px solid #3b82f6;
          color: #1e40af;
        }

        .mod-status-badge.inactive {
          background: var(--bg-secondary, #f1f5f9);
          border: 1px solid var(--border-color, #e2e8f0);
          color: var(--text-secondary, #64748b);
        }

        .loading-text, .no-data-text {
          font-size: 0.85rem;
          color: var(--text-secondary, #64748b);
          text-align: center;
          padding: 16px 0;
        }

        /* Card de estado */
        .status-card {
          border-left-color: #60a5fa;
        }

        .status-card .card-icon {
          color: #60a5fa;
        }

        .control-status {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.8rem;
          margin-bottom: 8px;
        }

        .control-status.enabled {
          background: #dbeafe;
          color: #1e40af;
        }

        .control-status.disabled {
          background: var(--bg-secondary, #f1f5f9);
          color: var(--text-secondary, #64748b);
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        .control-status.enabled .status-dot {
          background: #3b82f6;
        }

        .control-status.disabled .status-dot {
          background: #94a3b8;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }

        .status-description {
          font-size: 0.8rem;
          color: var(--text-secondary, #64748b);
          margin: 0 0 8px 0;
          line-height: 1.4;
        }

        .last-decision {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          background: var(--bg-secondary, #f8fafc);
          border-radius: 6px;
          margin-bottom: 8px;
        }

        .decision-label {
          font-size: 0.75rem;
          color: var(--text-secondary, #64748b);
        }

        .decision-value {
          font-size: 0.7rem;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 4px;
          text-transform: uppercase;
        }

        .decision-value.activate, .decision-value.locked {
          background: #dbeafe;
          color: #1e40af;
        }

        .decision-value.deactivate {
          background: #e0e7ff;
          color: #3730a3;
        }

        .decision-value.no_change, .decision-value.skipped {
          background: var(--bg-secondary, #f1f5f9);
          color: var(--text-secondary, #64748b);
        }

        .link-to-config {
          display: inline-block;
          font-size: 0.8rem;
          color: #3b82f6;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .link-to-config:hover {
          color: #2563eb;
          text-decoration: underline;
        }

        /* Banner informativo */
        .info-banner {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 16px;
          background: var(--bg-primary, #ffffff);
          border: 1px solid #bfdbfe;
          border-left: 4px solid #3b82f6;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .info-icon {
          flex-shrink: 0;
          color: #3b82f6;
          display: flex;
          align-items: center;
        }

        .info-banner p {
          margin: 0;
          font-size: 0.8rem;
          color: var(--text-primary, #1e293b);
          line-height: 1.4;
        }

        .notifications-container {
          position: fixed;
          top: 80px;
          right: 20px;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 360px;
        }

        .notification {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          animation: slideInNotification 0.3s ease-out;
          backdrop-filter: blur(10px);
        }

        @keyframes slideInNotification {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .notification-success {
          background: #eff6ff;
          border: 1px solid #3b82f6;
          border-left: 4px solid #2563eb;
          color: #1e40af;
        }

        .notification-error {
          background: #eff6ff;
          border: 1px solid #60a5fa;
          border-left: 4px solid #3b82f6;
          color: #1e40af;
        }

        .notification-warning {
          background: #eff6ff;
          border: 1px solid #93c5fd;
          border-left: 4px solid #60a5fa;
          color: #1e40af;
        }

        .notification-info {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-left: 4px solid #3b82f6;
          color: #1e40af;
        }

        .notification-icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          color: #3b82f6;
        }

        .notification-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .notification-title {
          font-weight: 600;
          font-size: 0.8rem;
        }

        .notification-message {
          font-size: 0.8rem;
          line-height: 1.4;
        }

        .notification-close {
          flex-shrink: 0;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.6;
          transition: all 0.2s;
          color: #3b82f6;
        }

        .notification-close:hover {
          opacity: 1;
          background: rgba(59, 130, 246, 0.1);
        }

        /* Modal de confirmación de eliminación */
        .confirm-delete-modal {
          text-align: center;
          max-width: 360px;
        }

        .confirm-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 12px;
          color: #3b82f6;
        }

        .confirm-delete-modal h3 {
          color: var(--text-primary, #1e293b);
          margin-bottom: 10px;
          font-size: 1rem;
        }

        .confirm-delete-modal p {
          color: var(--text-secondary, #64748b);
          margin-bottom: 6px;
          font-size: 0.85rem;
        }

        .confirm-warning-text {
          font-size: 0.8rem;
          color: var(--text-tertiary, #94a3b8) !important;
          font-style: italic;
        }

        .confirm-delete-modal .modal-actions {
          margin-top: 20px;
          justify-content: center;
        }

        .btn-danger {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-danger:hover {
          background: #2563eb;
        }

        .btn-danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Action bar buttons */
        .action-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }

        .btn-success {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-success:hover {
          background: #2563eb;
        }

        .btn-secondary {
          background: var(--bg-secondary, #f1f5f9);
          color: var(--text-primary, #1e293b);
          border: 1px solid var(--border-color, #e2e8f0);
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          background: var(--border-color, #e2e8f0);
        }

        /* Table buttons */
        .btn-icon {
          background: none;
          border: none;
          padding: 6px;
          border-radius: 4px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .btn-edit {
          color: #3b82f6;
        }

        .btn-edit:hover {
          background: #dbeafe;
        }

        .btn-delete {
          color: #64748b;
        }

        .btn-delete:hover {
          background: #f1f5f9;
          color: #3b82f6;
        }

        .btn-delete:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        /* Overrides section */
        .overrides-section {
          background: var(--bg-primary, #ffffff);
          border: 1px solid var(--border-color, #e2e8f0);
          border-radius: 8px;
          padding: 16px;
        }

        .overrides-section h3 {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-primary, #1e293b);
          margin: 0 0 12px 0;
        }

        .overrides-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }

        .overrides-table th {
          text-align: left;
          padding: 8px 12px;
          background: var(--bg-secondary, #f8fafc);
          color: var(--text-secondary, #64748b);
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid var(--border-color, #e2e8f0);
        }

        .overrides-table td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-color, #e2e8f0);
          color: var(--text-primary, #1e293b);
        }

        .overrides-table tr:last-child td {
          border-bottom: none;
        }

        .overrides-table .amount {
          font-weight: 600;
          color: #2563eb;
        }

        .overrides-table .actions {
          display: flex;
          gap: 4px;
        }

        .empty-state {
          text-align: center;
          padding: 24px;
          color: var(--text-secondary, #64748b);
        }

        .empty-state p {
          margin: 0 0 6px 0;
          font-size: 0.85rem;
        }

        .empty-hint {
          font-size: 0.8rem;
          color: var(--text-tertiary, #94a3b8) !important;
        }
      `}</style>
    </div>
  );
};
